import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconArrowRight } from '@wegic/icon';
import { RadioTab, RadioTabs } from '@wegic/ui';
import '@wegic/ui/dist/components/RadioTabs/index.css';
import classNames from 'classnames';
import { extractPreview, formatDate, hasNotificationMedia, isVideoCover } from '../helper';
import styles from './index.module.less';
function NotificationCardMedia(param) {
    let { notification, variant } = param;
    const withMedia = hasNotificationMedia(notification.coverType, notification.coverUrl);
    const video = isVideoCover(notification.coverType);
    const autoplay = video && variant === 'featured';
    if (!withMedia) {
        return /*#__PURE__*/ _jsx("div", {
            className: classNames(styles['media-fallback'], styles[`media-fallback-${variant}`], video && styles['media-fallback-video'])
        });
    }
    if (video) {
        return /*#__PURE__*/ _jsx("video", {
            className: classNames(styles.media, styles[`media-${variant}`]),
            src: notification.coverUrl || undefined,
            muted: true,
            loop: autoplay,
            autoPlay: autoplay,
            playsInline: true,
            preload: "metadata",
            controls: false,
            disablePictureInPicture: true
        });
    }
    return /*#__PURE__*/ _jsx("img", {
        className: classNames(styles.media, styles[`media-${variant}`]),
        src: notification.coverUrl || undefined,
        alt: "",
        loading: "lazy"
    });
}
function FeaturedNotificationCard(param) {
    let { notification, onSelect } = param;
    const preview = extractPreview(notification.content);
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        className: styles['featured-card'],
        onClick: ()=>onSelect(notification.id),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles['featured-media-wrapper'],
                children: /*#__PURE__*/ _jsx(NotificationCardMedia, {
                    notification: notification,
                    variant: "featured"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['featured-content'],
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['featured-title-row'],
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['title-group'],
                                children: [
                                    !notification.isRead && /*#__PURE__*/ _jsx("span", {
                                        className: styles['unread-dot']
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: styles['featured-title'],
                                        children: notification.title
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: styles['featured-date'],
                                children: formatDate(notification.publishedAt)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['featured-arrow-wrapper'],
                                children: /*#__PURE__*/ _jsx(IconArrowRight, {
                                    fontSize: 16
                                })
                            })
                        ]
                    }),
                    notification.subtitle && /*#__PURE__*/ _jsx("div", {
                        className: styles['featured-subtitle'],
                        children: notification.subtitle
                    }),
                    preview && /*#__PURE__*/ _jsx("div", {
                        className: styles['featured-preview'],
                        children: preview
                    })
                ]
            })
        ]
    });
}
function CompactNotificationCard(param) {
    let { notification, onSelect } = param;
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        className: styles['compact-card'],
        onClick: ()=>onSelect(notification.id),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles['compact-media-wrapper'],
                children: /*#__PURE__*/ _jsx(NotificationCardMedia, {
                    notification: notification,
                    variant: "compact"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['compact-content'],
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles['compact-title-row'],
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['title-group'],
                                children: [
                                    !notification.isRead && /*#__PURE__*/ _jsx("span", {
                                        className: styles['unread-dot']
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: styles['compact-title'],
                                        children: notification.title
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: styles['compact-date'],
                                children: formatDate(notification.publishedAt)
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['compact-arrow-wrapper'],
                                children: /*#__PURE__*/ _jsx(IconArrowRight, {
                                    fontSize: 16
                                })
                            })
                        ]
                    }),
                    notification.subtitle && /*#__PURE__*/ _jsx("div", {
                        className: styles['compact-subtitle'],
                        children: notification.subtitle
                    })
                ]
            })
        ]
    });
}
function NotificationList(param) {
    let { notifications, loading, activeTab, updateUnread, forYouUnread, onSwitchTab, onSelect, onLoadMore } = param;
    const updateLabel = intl.get('workspace.notifications.tab.update');
    const forYouLabel = intl.get('workspace.notifications.tab.forYou');
    const updateEmptyTitle = intl.get('workspace.notifications.empty.update.title');
    const updateEmptyDescription = intl.get('workspace.notifications.empty.update.description');
    const forYouEmptyTitle = intl.get('workspace.notifications.empty.specific.title');
    const forYouEmptyDescription = intl.get('workspace.notifications.empty.specific.description');
    const handleScroll = (e)=>{
        const target = e.currentTarget;
        if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
            onLoadMore();
        }
    };
    const showFeaturedCard = activeTab === 'update';
    const featuredNotification = showFeaturedCard ? notifications[0] : null;
    const regularNotifications = showFeaturedCard ? notifications.slice(1) : notifications;
    const emptyTitle = activeTab === 'update' ? updateEmptyTitle : forYouEmptyTitle;
    const emptyDescription = activeTab === 'update' ? updateEmptyDescription : forYouEmptyDescription;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.container,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles['tabs-wrapper'],
                children: /*#__PURE__*/ _jsxs(RadioTabs, {
                    value: activeTab,
                    defaultValue: "update",
                    onChange: onSwitchTab,
                    size: "small",
                    className: styles.tabs,
                    children: [
                        /*#__PURE__*/ _jsxs(RadioTab, {
                            value: "update",
                            children: [
                                updateLabel,
                                updateUnread > 0 ? ` (${updateUnread})` : ''
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(RadioTab, {
                            value: "for_you",
                            children: [
                                forYouLabel,
                                forYouUnread > 0 ? ` (${forYouUnread})` : ''
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.list,
                onScroll: handleScroll,
                children: [
                    loading && notifications.length === 0 && /*#__PURE__*/ _jsx("div", {
                        className: styles.loading,
                        children: intl.get('common.text.loading')
                    }),
                    !loading && notifications.length === 0 && /*#__PURE__*/ _jsxs("div", {
                        className: styles.empty,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['empty-title'],
                                children: emptyTitle
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles['empty-description'],
                                children: emptyDescription
                            })
                        ]
                    }),
                    featuredNotification && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(FeaturedNotificationCard, {
                                notification: featuredNotification,
                                onSelect: onSelect
                            }),
                            regularNotifications.length > 0 && /*#__PURE__*/ _jsx("div", {
                                className: styles.divider
                            })
                        ]
                    }),
                    regularNotifications.map((item, index)=>/*#__PURE__*/ _jsxs("div", {
                            className: styles['regular-item'],
                            children: [
                                /*#__PURE__*/ _jsx(CompactNotificationCard, {
                                    notification: item,
                                    onSelect: onSelect
                                }),
                                index < regularNotifications.length - 1 && /*#__PURE__*/ _jsx("div", {
                                    className: styles.divider
                                })
                            ]
                        }, item.id)),
                    loading && notifications.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: styles['loading-more'],
                        children: intl.get('common.text.loading')
                    })
                ]
            })
        ]
    });
}
export default NotificationList;
